import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { Input, Label } from 'reactstrap';
import { TypedMemo } from '../../../../../common/types';
import { getFullAPIFileURL } from '../../../../../common/getFullAPIFileURL';
import { platformName } from '../../../platformMeta';
import { isSocialPlatform } from '../../../types';
import { ChannelPill } from './ChannelPill';
import { useRollout } from './RolloutContext';
import type { PlanStepView, StepChannelView } from './rolloutModel';
import { rolloutMessages } from './rolloutMessages';
import { sheetMessages } from './sheetMessages';
import { isoOfWallTime, wallTimeOf, zoneCity } from './rolloutTime';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the run sheet; each part uses its own slice of it */
import styles from './Rollout.module.scss';
import sheetStyles from './StepSheet.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The states whose time the artist may change (`POST promo/posts/:uuid/move`). */
const MOVABLE = ['draft', 'scheduled', 'needsYou', 'failed'];

interface StepSheetPostProps {
  step: PlanStepView;
  channel: StepChannelView;
}

/**
 * The post of one channel of a step (design rollout-plan c1): the preview (the file and the caption
 * of the post on that channel), the time in the artist's time zone with a picker, and the state.
 */
const StepSheetPostComponent: React.FC<StepSheetPostProps> = ({ step, channel }) => {
  const intl = useIntl();
  const { actions, timeZone, canAct } = useRollout();
  const name = isSocialPlatform(channel.platform) ? platformName(channel.platform) : channel.platform;
  const [wall, setWall] = useState(() => wallTimeOf(channel.at, timeZone));
  useEffect(() => setWall(wallTimeOf(channel.at, timeZone)), [channel.at, timeZone]);
  const poster = step.day.asset?.posterUrl ?? null;
  const { post } = channel;
  const canMove = canAct && post !== null && MOVABLE.includes(channel.state);
  const city = zoneCity(timeZone);
  const feedback = actions.feedback.sheet ?? null;

  const saveTime = () => {
    const iso = isoOfWallTime(wall, timeZone);
    if (post === null || iso === null || iso === new Date(channel.at).toISOString()) {
      return;
    }
    actions.movePostTo('sheet', post.uuid, iso).catch(console.error);
  };

  return (
    <div className={sheetStyles.preview}>
      <div className={sheetStyles.phone}>
        <div className={sheetStyles.phoneHead}>{name}</div>
        <div className={sheetStyles.phoneImage}>
          {poster !== null && <img src={getFullAPIFileURL(poster)} alt="" />}
        </div>
        {post !== null && post.caption !== '' && <div className={sheetStyles.caption}>{post.caption}</div>}
      </div>
      <div className={sheetStyles.fields}>
        <div className={sheetStyles.field}>
          <Label for="rollout-step-time" className={sheetStyles.label}>
            {intl.formatMessage(sheetMessages.goesOut, { name })}
          </Label>
          <Input
            id="rollout-step-time"
            type="datetime-local"
            value={wall}
            disabled={!canMove || actions.busy !== null}
            onChange={(event) => setWall(event.target.value)}
            onBlur={saveTime}
          />
          {city !== null && (
            <small className={styles.line}>{intl.formatMessage(sheetMessages.zoneNote, { name, zone: city })}</small>
          )}
        </div>
        <div className={sheetStyles.field}>
          <span className={sheetStyles.label}>{intl.formatMessage(sheetMessages.state)}</span>
          <span>
            <ChannelPill channel={channel} showTime={false} />
          </span>
        </div>
        <div className={sheetStyles.field}>
          <span className={sheetStyles.label}>{intl.formatMessage(sheetMessages.caption)}</span>
          <span className={styles.line}>{intl.formatMessage(sheetMessages.captionNote)}</span>
        </div>
        {feedback !== null && (
          <p className={clsx(styles.feedback, feedback.tone === 'danger' && styles.feedbackBad)}>{feedback.message}</p>
        )}
        {channel.state === 'notPlanned' && (
          <span className={styles.line}>{intl.formatMessage(rolloutMessages.pillNotPlanned)}</span>
        )}
      </div>
    </div>
  );
};

export const StepSheetPost = TypedMemo(StepSheetPostComponent);
